import { inject, Injectable } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import { RouterNavigated } from '@ngxs/router-plugin';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { ReentryBriefResult, ReviewComment, ScreenerQueueFilter, ScreenerQueueFilterOptions, ScreenerQueueHttpService } from '@tsin-core/services/http/screener-queue.service';
import { SaveAdminStep } from '@tsin-core/states/applications/applications.actions';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import {
  AddReviewComment,
  AssignScreener,
  AutoDistribute,
  ClearReviewComments,
  ClearScreenerQueue,
  DismissReentryBrief,
  FinalizeDecision,
  InitiateQCReview,
  LoadApplicationForProcessing,
  LoadCDReviewQueue,
  LoadManagerReviewQueue,
  LoadQCApplication,
  LoadQCQueue,
  LoadReentryBrief,
  LoadReviewComments,
  LoadScreenerQueue,
  LoadScreenerQueueApplication,
  LoadScreenerStats,
  PullFromDraw,
  ReopenDecision,
  SetScreenerQueueFilter,
  SubmitQCReview,
  SubmitScreeningDecision,
  TriggerDocumentValidation,
  TriggerKyeForDraw,
  TriggerResumeValidation,
  UnassignScreener,
  UpdateBriefDecision,
  UpdateQueueStatus,
} from './screener-queue.actions';

/** `RouterNavigated` is declared generic in its router-state type, which
 *  leaves NGXS unable to infer the handler's payload from the class alone.
 *  Pinning the type argument on an alias restores it, and NGXS matches
 *  handlers on the `type` string, so the alias binds the same action.
 *  Alias typé de `RouterNavigated` : le générique empêche NGXS d'inférer la
 *  charge utile du gestionnaire. */
type RouterNavigatedAction = RouterNavigated<RouterStateSnapshot>;
const RouterNavigatedAction: { type: string; new (...args: any[]): RouterNavigatedAction } = RouterNavigated;

/** Root of the screener queue's route subtree — the queue list, its sibling
 *  QC/CD/manager queues and every application opened from them.
 *  Racine de l'arborescence de routes de la file d'examen. */
const SCREENER_QUEUE_ROUTE = '/management/screener-queue';

/**
 * Whether `url` sits inside the screener queue's route subtree. Query string
 * and fragment are dropped first, so `/…/list?page=2` still counts; a sibling
 * path that merely starts with the same characters does not.
 *
 * Indique si l'URL appartient à l'arborescence de la file d'examen.
 */
export function isScreenerQueueUrl(url: string | null | undefined): boolean {
  const path = (url ?? '').split(/[?#]/)[0];
  return path === SCREENER_QUEUE_ROUTE || path.startsWith(`${SCREENER_QUEUE_ROUTE}/`);
}

export class ScreenerQueueStateModel {
  queueItems: any[] = [];
  total: number = 0;
  page: number = 1;
  limit: number = 25;
  /** Choices of the queue table's open-list column filters, sent with each page.
   *  Choix des filtres de colonne à liste ouverte, reçus avec chaque page. */
  filterOptions: ScreenerQueueFilterOptions = { applicationStatus: [], eligibilityStatus: [], decisionMade: [] };
  /** The cohort/draw scope the screener queue was last left in. Merged into
   *  every `LoadScreenerQueue` request, so moving around inside the queue —
   *  opening an application, stepping through it, coming back for the next
   *  one — reloads the list where the admin left it. It lives only as long as
   *  the route subtree does: any navigation out of `/management/screener-queue`
   *  drops it (see the `RouterNavigated` handler below). Persisted to session
   *  storage (see `app.config.ts`), so a refresh inside the queue keeps it too.
   *  Portée (cohorte/tirage) de la file, fusionnée dans chaque chargement,
   *  conservée tant que l'on reste dans l'arborescence de la file. */
  filter: ScreenerQueueFilter = {};
  selectedApplication: any = null;
  stats: any[] = [];
  qcQueue: any[] = [];
  cdReviewQueue: any[] = [];
  /** Phase 3A — Applications with preliminary decisions awaiting manager sign-off.
   *  Candidatures avec décisions préliminaires en attente de validation du gestionnaire. */
  managerReviewQueue: any[] = [];
  selectedQCApplication: any = null;
  loading: LoadingState = LoadingState.notLoading;
  /** B4 — Re-entry brief for the currently open application / Résumé de reprise */
  reentryBrief: ReentryBriefResult | null = null;
  /** Whether the re-entry brief banner is dismissed by the user / Bannière masquée */
  reentryBriefDismissed: boolean = false;
  /** B5 — Document validation state (Pass/Flag/Fail) for the open application / État de validation des documents */
  documentValidationLoading: boolean = false;
  /** B6 — Resume validation state (Pass/Flag/Fail) for the open application / État de conformité du CV */
  resumeValidationLoading: boolean = false;
  /** Application loaded for the screener processing view / Candidature chargée pour la vue de traitement */
  processingApplication: any = null;
  /** Whether a screening decision submission is in progress / Soumission de décision en cours */
  decisionSubmitting: boolean = false;
  /** Internal staff review thread for the open application, newest first.
   *  Fil de commentaires internes de la candidature ouverte. */
  reviewComments: ReviewComment[] = [];
  /** Whether the review thread is being fetched / Chargement du fil en cours */
  reviewCommentsLoading: boolean = false;
  /** Whether a comment is being posted / Publication d'un commentaire en cours */
  reviewCommentSaving: boolean = false;
  /** Phase 1D — Whether a finalize-decision call is in progress / Finalisation de décision en cours */
  finalizingDecision: boolean = false;
  /** Whether a reopen-decision call is in progress / Réouverture de décision en cours */
  reopeningDecision: boolean = false;
  /** Result of the last pull-from-draw operation — used to display the correct activated count.
   *  Résultat du dernier tirage activé — utilisé pour afficher le nombre correct de candidatures activées. */
  lastPullResult: { activated: number; drawName: string } | null = null;
  /** Result of the last auto-distribute operation — used to display the correct assigned count in the success toast.
   *  Résultat de la dernière distribution automatique — utilisé pour afficher le nombre correct dans le message de succès. */
  lastAutoDistributeResult: { assigned: number; total: number } | null = null;
}

const defaults: ScreenerQueueStateModel = {
  queueItems: [],
  total: 0,
  page: 1,
  limit: 25,
  filterOptions: { applicationStatus: [], eligibilityStatus: [], decisionMade: [] },
  filter: {},
  selectedApplication: null,
  stats: [],
  qcQueue: [],
  cdReviewQueue: [],
  managerReviewQueue: [],
  selectedQCApplication: null,
  loading: LoadingState.notLoading,
  reentryBrief: null,
  reentryBriefDismissed: false,
  documentValidationLoading: false,
  resumeValidationLoading: false,
  processingApplication: null,
  decisionSubmitting: false,
  reviewComments: [],
  reviewCommentsLoading: false,
  reviewCommentSaving: false,
  finalizingDecision: false,
  reopeningDecision: false,
  lastPullResult: null,
  lastAutoDistributeResult: null,
};

@State<ScreenerQueueStateModel>({ name: 'screenerQueueState', defaults })
@Injectable()
export class ScreenerQueueState {
  private readonly svc = inject(ScreenerQueueHttpService);

  @Selector() static getQueueItems(s: ScreenerQueueStateModel) {
    return s.queueItems;
  }
  @Selector() static getTotal(s: ScreenerQueueStateModel) {
    return s.total;
  }
  @Selector() static getFilterOptions(s: ScreenerQueueStateModel) {
    return s.filterOptions;
  }
  @Selector() static getFilter(s: ScreenerQueueStateModel) {
    return s.filter;
  }
  @Selector() static getSelectedApplication(s: ScreenerQueueStateModel) {
    return s.selectedApplication;
  }
  @Selector() static getStats(s: ScreenerQueueStateModel) {
    return s.stats;
  }
  @Selector() static getQCQueue(s: ScreenerQueueStateModel) {
    return s.qcQueue;
  }
  @Selector() static getCDReviewQueue(s: ScreenerQueueStateModel) {
    return s.cdReviewQueue;
  }
  /** Selector for Phase 3A manager review queue / Sélecteur pour la file du gestionnaire (Phase 3A) */
  @Selector() static getManagerReviewQueue(s: ScreenerQueueStateModel) {
    return s.managerReviewQueue;
  }
  @Selector() static getSelectedQCApplication(s: ScreenerQueueStateModel) {
    return s.selectedQCApplication;
  }
  @Selector() static getLoading(s: ScreenerQueueStateModel) {
    return s.loading;
  }
  @Selector() static getReentryBrief(s: ScreenerQueueStateModel) {
    return s.reentryBrief;
  }
  @Selector() static getReentryBriefDismissed(s: ScreenerQueueStateModel) {
    return s.reentryBriefDismissed;
  }
  /** Application loaded for screener processing / Candidature pour le traitement */
  @Selector() static getProcessingApplication(s: ScreenerQueueStateModel) {
    return s.processingApplication;
  }
  /** Whether a screening decision is being submitted / Soumission en cours */
  @Selector() static getDecisionSubmitting(s: ScreenerQueueStateModel) {
    return s.decisionSubmitting;
  }
  /** Internal staff review thread for the open application / Fil de commentaires internes */
  @Selector() static getReviewComments(s: ScreenerQueueStateModel) {
    return s.reviewComments;
  }
  /** Whether the review thread is loading / Chargement du fil en cours */
  @Selector() static getReviewCommentsLoading(s: ScreenerQueueStateModel) {
    return s.reviewCommentsLoading;
  }
  /** Whether a comment is being posted / Publication d'un commentaire en cours */
  @Selector() static getReviewCommentSaving(s: ScreenerQueueStateModel) {
    return s.reviewCommentSaving;
  }
  /** Phase 1D — Whether a finalize-decision call is in progress / Finalisation de décision en cours */
  @Selector() static getFinalizingDecision(s: ScreenerQueueStateModel) {
    return s.finalizingDecision;
  }
  /** Whether a reopen-decision call is in progress / Réouverture de décision en cours */
  @Selector() static getReopeningDecision(s: ScreenerQueueStateModel) {
    return s.reopeningDecision;
  }

  @Action(SetScreenerQueueFilter)
  setFilter({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: SetScreenerQueueFilter) {
    patchState({ filter });
  }

  /**
   * Drop the remembered cohort/draw scope as soon as a navigation lands
   * outside the screener queue's route subtree. The scope is there to carry an
   * admin between the queue and the applications it opens, not to follow them
   * around the rest of the app: leaving for another management page, the
   * dashboard or a logout means the queue opens unscoped on the next visit.
   *
   * Reacts to `RouterNavigated` rather than `RouterNavigation` so a navigation
   * a guard cancels leaves the scope alone — nowhere was actually left.
   *
   * Efface la portée mémorisée dès qu'une navigation quitte l'arborescence de
   * la file d'examen ; `RouterNavigated` (navigation aboutie) plutôt que
   * `RouterNavigation`, afin qu'une navigation annulée ne change rien.
   */
  @Action(RouterNavigatedAction)
  forgetScopeOffQueue({ getState, patchState }: StateContext<ScreenerQueueStateModel>, { routerState }: RouterNavigatedAction) {
    if (isScreenerQueueUrl(routerState?.url)) return;
    const { cohortId, drawId } = getState().filter;
    if (!cohortId && !drawId) return;
    patchState({ filter: {} });
  }

  @Action(ClearScreenerQueue)
  clear({ setState }: StateContext<ScreenerQueueStateModel>) {
    setState(defaults);
  }

  @Action(LoadScreenerQueue)
  loadQueue({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadScreenerQueue) {
    patchState({ loading: LoadingState.loadingList });
    const merged = { ...getState().filter, ...filter };
    return this.svc.getQueue(merged).pipe(
      tap(({ data, total, page, limit, filterOptions }) =>
        patchState({ queueItems: data, total, page, limit, filterOptions: filterOptions ?? getState().filterOptions, loading: LoadingState.notLoading }),
      ),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadScreenerQueueApplication)
  loadApplication({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadScreenerQueueApplication) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getApplication(id).pipe(
      tap((selectedApplication) => patchState({ selectedApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadScreenerStats)
  loadStats({ patchState }: StateContext<ScreenerQueueStateModel>, { cohortId }: LoadScreenerStats) {
    return this.svc.getStats(cohortId).pipe(tap((stats) => patchState({ stats })));
  }

  @Action(AssignScreener)
  assignScreener({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: AssignScreener) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.assignScreener(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UnassignScreener)
  unassignScreener({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UnassignScreener) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.unassignScreener(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UpdateQueueStatus)
  updateStatus({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UpdateQueueStatus) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.updateQueueStatus(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(AutoDistribute)
  autoDistribute({ patchState }: StateContext<ScreenerQueueStateModel>, { payload }: AutoDistribute) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.autoDistribute(payload).pipe(
      tap((res) => patchState({ lastAutoDistributeResult: res, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadQCQueue)
  loadQCQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadQCQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getQCQueue(filter).pipe(
      tap((qcQueue) => patchState({ qcQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadCDReviewQueue)
  loadCDReviewQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadCDReviewQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getCDReviewQueue(filter).pipe(
      tap((cdReviewQueue) => patchState({ cdReviewQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  /** Phase 3A — Load applications with preliminary decisions for manager sign-off.
   *  Phase 3A — Charger les candidatures avec décisions préliminaires pour validation du gestionnaire. */
  @Action(LoadManagerReviewQueue)
  loadManagerReviewQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadManagerReviewQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getManagerReviewQueue(filter).pipe(
      tap((managerReviewQueue) => patchState({ managerReviewQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadQCApplication)
  loadQCApplication({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadQCApplication) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getQCApplication(id).pipe(
      tap((selectedQCApplication) => patchState({ selectedQCApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(InitiateQCReview)
  initiateQCReview({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id }: InitiateQCReview) {
    return this.svc.initiateQCReview(id).pipe(
      tap((updated) => {
        const qcQueue = getState().qcQueue.map((a) => (a._id === id ? updated : a));
        patchState({ qcQueue, selectedQCApplication: updated });
      }),
    );
  }

  @Action(SubmitQCReview)
  submitQCReview({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: SubmitQCReview) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.submitQCReview(id, payload).pipe(
      tap((updated) => {
        const qcQueue = getState().qcQueue.map((a) => (a._id === id ? updated : a));
        patchState({ qcQueue, selectedQCApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UpdateBriefDecision)
  updateBriefDecision({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UpdateBriefDecision) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.updateBriefDecision(id, payload).pipe(
      tap((updated) => {
        const cdReviewQueue = getState().cdReviewQueue.map((a) => (a._id === id ? updated : a));
        patchState({ cdReviewQueue, selectedQCApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  // ─── B4: AI Screening Brief Actions ────────────────────────────────────────

  /** Fetch (or generate) the re-entry brief for a given application.
   *  Récupère (ou génère) le résumé de reprise pour une candidature donnée. */
  @Action(LoadReentryBrief)
  loadReentryBrief({ patchState }: StateContext<ScreenerQueueStateModel>, { applicationId }: LoadReentryBrief) {
    patchState({ reentryBrief: null, reentryBriefDismissed: false, loading: LoadingState.loadingList });
    return this.svc.getReentryBrief(applicationId).pipe(
      tap((brief) => patchState({ reentryBrief: brief, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        // Non-fatal: brief load failure should not break the application view
        // Non fatal : l'échec de chargement du résumé ne doit pas bloquer la vue
        console.warn('[ScreenerQueueState] Re-entry brief load failed:', e);
        return [];
      }),
    );
  }

  /** Mark the re-entry brief banner as dismissed (session-local).
   *  Marquer la bannière du résumé de reprise comme masquée. */
  @Action(DismissReentryBrief)
  dismissReentryBrief({ patchState }: StateContext<ScreenerQueueStateModel>) {
    patchState({ reentryBriefDismissed: true });
  }

  // ─── B5: AI Document Validation ────────────────────────────────────────────

  @Selector()
  static getDocumentValidationLoading(s: ScreenerQueueStateModel) {
    return s.documentValidationLoading;
  }

  /** Trigger document validation; refreshes the selected application on completion.
   *  Déclencher la validation des documents; rafraîchit la candidature sélectionnée à la fin. */
  @Action(TriggerDocumentValidation)
  triggerDocumentValidation({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id }: TriggerDocumentValidation) {
    patchState({ documentValidationLoading: true });
    return this.svc.triggerDocumentValidation(id).pipe(
      tap(() => {
        patchState({ documentValidationLoading: false });
        dispatch(new LoadScreenerQueueApplication(id));
      }),
      catchError((e) => {
        patchState({ documentValidationLoading: false });
        console.warn('[ScreenerQueueState] Document validation failed:', e);
        throw e;
      }),
    );
  }

  // ─── B6: Resume Template Compliance ────────────────────────────────────────

  @Selector()
  static getResumeValidationLoading(s: ScreenerQueueStateModel) {
    return s.resumeValidationLoading;
  }

  /** Trigger resume validation; refreshes the selected application on completion.
   *  Déclencher la validation du CV; rafraîchit la candidature sélectionnée à la fin. */
  @Action(TriggerResumeValidation)
  triggerResumeValidation({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id }: TriggerResumeValidation) {
    patchState({ resumeValidationLoading: true });
    return this.svc.triggerResumeValidation(id).pipe(
      tap(() => {
        patchState({ resumeValidationLoading: false });
        dispatch(new LoadScreenerQueueApplication(id));
      }),
      catchError((e) => {
        patchState({ resumeValidationLoading: false });
        console.warn('[ScreenerQueueState] Resume validation failed:', e);
        throw e;
      }),
    );
  }

  // ─── Screening Workflow ────────────────────────────────────────────────────

  /** Load a single application for the screener processing detail view.
   *  Charger une candidature pour la vue de traitement de l'examinateur. */
  @Action(LoadApplicationForProcessing)
  loadApplicationForProcessing({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadApplicationForProcessing) {
    patchState({ loading: LoadingState.loadingList, processingApplication: null });
    return this.svc.getApplication(id).pipe(
      tap((processingApplication) => patchState({ processingApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  // ─── Internal review comments ──────────────────────────────────────────────

  @Action(LoadReviewComments)
  loadReviewComments({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadReviewComments) {
    patchState({ reviewCommentsLoading: true });
    return this.svc.getReviewComments(id).pipe(
      tap((reviewComments) => patchState({ reviewComments, reviewCommentsLoading: false })),
      catchError((e) => {
        // Leave the thread empty rather than stale — a failed reload must not
        // leave another application's comments on screen.
        // Vider le fil plutôt que de laisser des données périmées.
        patchState({ reviewComments: [], reviewCommentsLoading: false });
        throw e;
      }),
    );
  }

  @Action(AddReviewComment)
  addReviewComment({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: AddReviewComment) {
    patchState({ reviewCommentSaving: true });
    return this.svc.addReviewComment(id, payload).pipe(
      tap((comment) =>
        // Prepend — the thread is newest-first, and the server returns the
        // entry it stored, so no reload is needed to show the new comment.
        // Ajout en tête : le fil est trié du plus récent au plus ancien.
        patchState({ reviewComments: [comment, ...getState().reviewComments], reviewCommentSaving: false }),
      ),
      catchError((e) => {
        patchState({ reviewCommentSaving: false });
        throw e;
      }),
    );
  }

  @Action(ClearReviewComments)
  clearReviewComments({ patchState }: StateContext<ScreenerQueueStateModel>) {
    patchState({ reviewComments: [], reviewCommentsLoading: false, reviewCommentSaving: false });
  }

  /** Pull applications from a draw into the screener queue (status = Pending).
   *  Activer les candidatures d'un tirage dans la file d'attente (statut = Pending). */
  @Action(PullFromDraw)
  pullFromDraw({ patchState }: StateContext<ScreenerQueueStateModel>, { payload }: PullFromDraw) {
    return this.svc.pullFromDraw(payload).pipe(
      tap((result) => patchState({ lastPullResult: { activated: result.activated, drawName: result.drawName } })),
      catchError((e) => {
        throw e;
      }),
    );
  }

  /** Optimistically sync admin step data into the processing application so the
   *  step view immediately reflects saved notes without a round-trip reload.
   *  Synchronisation optimiste des données admin dans la candidature en traitement. */
  @Action(SaveAdminStep)
  syncAdminStepToProcessingApp({ getState, patchState }: StateContext<ScreenerQueueStateModel>, { payload }: SaveAdminStep) {
    const { processingApplication } = getState();
    if (!processingApplication) return;
    const steps = (processingApplication.steps ?? []).map((s: any) => (s.name === payload.step ? { ...s, adminData: payload.data } : s));
    patchState({ processingApplication: { ...processingApplication, steps } });
  }

  /** Manually (re-)trigger KYE agents for all applications in an activated draw.
   *  fire-and-forget: async event emission on the server — no state change needed.
   *  Déclencher manuellement les agents KYE pour toutes les candidatures d'un tirage activé. */
  @Action(TriggerKyeForDraw)
  triggerKyeForDraw(_: StateContext<ScreenerQueueStateModel>, { drawId, force, retrospective }: TriggerKyeForDraw) {
    return this.svc.triggerKyeForDraw(drawId, force, retrospective);
  }

  /** Submit a preliminary screening decision and route the application.
   *  Soumettre une décision préliminaire et acheminer la candidature. */
  @Action(SubmitScreeningDecision)
  submitScreeningDecision({ patchState }: StateContext<ScreenerQueueStateModel>, { id, payload }: SubmitScreeningDecision) {
    patchState({ decisionSubmitting: true });
    return this.svc.submitScreeningDecision(id, payload).pipe(
      // Re-read the application so the decision timeline shows the entry that
      // was just recorded, along with its reasons/conditions/criteria — the
      // submit response carries only the new routing status, not the document.
      //
      // Done here rather than by re-dispatching LoadApplicationForProcessing:
      // that action blanks processingApplication and raises the list spinner,
      // which would flash the whole processing view away on every decision.
      //
      // A failed refresh must never report a recorded decision as failed, so it
      // is swallowed and the original result still emitted — the timeline just
      // stays stale until the next load.
      //
      // Relit la candidature pour que la chronologie affiche l'entrée qui vient
      // d'être enregistrée ; un échec du rafraîchissement est ignoré.
      switchMap((result) =>
        this.svc.getApplication(id).pipe(
          tap((processingApplication) => patchState({ processingApplication })),
          catchError(() => of(null)),
          map(() => result),
        ),
      ),
      tap(() => patchState({ decisionSubmitting: false })),
      catchError((e) => {
        patchState({ decisionSubmitting: false });
        throw e;
      }),
    );
  }

  /** The application as the processing page loads it, read again after a
   *  finalize or reopen. Those calls answer with the bare stored document — no
   *  applicant or exam names, no `queuePhase` or `finalizedPhases` — so showing
   *  it switched a Phase 2 review back to Phase 1's steps and left the decision
   *  form's lock stale. Falls back to that document if the read fails: the
   *  action itself succeeded.
   *  La candidature relue après une finalisation ou une réouverture. */
  private reloadProcessingApplication(id: string, fallback: unknown) {
    return this.svc.getApplication(id).pipe(catchError(() => of(fallback)));
  }

  /** Phase 1D — Finalize the most recent preliminary decision and refresh the processing view.
   *  Phase 1D — Finaliser la décision préliminaire la plus récente et rafraîchir la vue de traitement. */
  @Action(FinalizeDecision)
  finalizeDecision({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id, payload }: FinalizeDecision) {
    patchState({ finalizingDecision: true });
    return this.svc.finalizeDecision(id, payload).pipe(
      switchMap((updated) => this.reloadProcessingApplication(id, updated)),
      tap((processingApplication) => {
        patchState({ finalizingDecision: false, processingApplication });
        // Refresh the manager review queue so finalized apps disappear from it
        dispatch(new LoadManagerReviewQueue());
      }),
      catchError((e) => {
        patchState({ finalizingDecision: false });
        throw e;
      }),
    );
  }

  /** Reopen a finalized decision and refresh the processing view — the inverse
   *  of finalizeDecision.
   *  Rouvrir une décision finalisée et rafraîchir la vue de traitement. */
  @Action(ReopenDecision)
  reopenDecision({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id, payload }: ReopenDecision) {
    patchState({ reopeningDecision: true });
    return this.svc.reopenApplication(id, payload).pipe(
      switchMap((updated) => this.reloadProcessingApplication(id, updated)),
      tap((processingApplication) => {
        patchState({ reopeningDecision: false, processingApplication });
        // Refresh the manager review queue in case the reopened application
        // had been sitting there awaiting sign-off.
        dispatch(new LoadManagerReviewQueue());
      }),
      catchError((e) => {
        patchState({ reopeningDecision: false });
        throw e;
      }),
    );
  }
}
